import { useCallback, useState } from 'react'
import { submitFinalEnrollmentRequest } from '@/api/enrollment-api'
import { ENROLLMENT_COMMON_MESSAGES } from '../../../../constants/enrollmentContent'
import { SELF_ENROLLMENT_STEPS } from '../../utils/steps'
import { getRequestErrorMessage } from '../../utils/utils'
import type { ApplyHydrationFn } from '../../types'

type UseEnrollmentFinalizationArgs = {
  enrollmentSessionToken: string | null
  applyHydration: ApplyHydrationFn
  goToStep: (step: number) => void
  navigate: (to: string, options?: { replace?: boolean }) => void
  setStepSubmissionError: (value: string | null) => void
}

export function useEnrollmentFinalization({
  enrollmentSessionToken,
  applyHydration,
  goToStep,
  navigate,
  setStepSubmissionError
}: UseEnrollmentFinalizationArgs) {
  const [isFinalizingEnrollment, setIsFinalizingEnrollment] = useState(false)

  const handleFinalizeEnrollment = useCallback(async () => {
    if (!enrollmentSessionToken) {
      setStepSubmissionError(ENROLLMENT_COMMON_MESSAGES.sessionExpired)
      navigate('/application', { replace: true })
      return
    }

    setStepSubmissionError(null)
    setIsFinalizingEnrollment(true)

    try {
      const response = await submitFinalEnrollmentRequest(enrollmentSessionToken)

      applyHydration({ enrollment: response, syncStep: false })
      goToStep(SELF_ENROLLMENT_STEPS.ENROLLMENT_COMPLETE)
    } catch (error) {
      setStepSubmissionError(
        getRequestErrorMessage(error, ENROLLMENT_COMMON_MESSAGES.submitFailed)
      )
    } finally {
      setIsFinalizingEnrollment(false)
    }
  }, [applyHydration, enrollmentSessionToken, goToStep, navigate, setStepSubmissionError])

  return {
    handleFinalizeEnrollment,
    isFinalizingEnrollment
  }
}
